import {useEffect, useState} from 'react'
import Chart from 'react-apexcharts'
import {DatePicker} from '@material-ui/pickers'
import {format, isValid, parseISO} from 'date-fns'
import {useTranslation} from 'react-i18next'

import {KTSVG} from '../../../../_metronic/helpers'
import AppSelect from '../../../shared/AppSelect'
import {AGRONOMIC_CROP_IDS, getCropAnalysisMonths, getCropLabel, getObservationalProfileId, isObservationalCrop} from '../../../shared/utils/cropCatalog'
import '../Fields/ViewField/ViewLand/Images/DateSelector.style.scss'
import {buildCumulativeWaterData, summarizeAccumulatedClimaticDeficit} from './waterBalanceChart'
import {withPdfChartReadiness} from './pdfVisualReadiness'
import {
  areAgronomicProfilesEqual,
  buildVegetationIndexChartData,
  getAgronomicSowingDateStatus,
  getAgronomicToday,
  getAgronomicFieldObservationStageIds,
  getMaximumAgronomicAnalysisEndDate,
  getPhenologyTemporalReference,
  hasCompleteAgronomicProfile,
  hasValidAgronomicAnalysisPeriod,
  hasValidAgronomicFieldObservations,
  hasValidThermalOnlyTime,
  hasValidThermalTime,
  isThermalOnlyAgronomicProfile,
  THERMAL_REFERENCE_PROFILES,
} from './copilotModel'

const EMPTY_PROFILE = {
  crop: '',
  genotypeId: '',
  sowingDate: '',
  harvestDate: '',
  thermalTime: null,
}

const isFiniteNumber = (value) =>
  value !== '' && value !== null && value !== undefined && Number.isFinite(Number(value))

const clonePhotoperiod = (value) => {
  if (!value || typeof value !== 'object') {
    return null
  }

  return {
    enabled: value.enabled,
    responseType: value.responseType,
    criticalHours: value.criticalHours,
    sensitivityPerHour: value.sensitivityPerHour,
    minimumFactor: value.minimumFactor,
    appliesFromStageId: value.appliesFromStageId,
    appliesUntilStageId: value.appliesUntilStageId,
  }
}

const cloneVernalization = (value) => {
  if (!value || typeof value !== 'object') {
    return null
  }

  return {
    enabled: value.enabled,
    minimumTemperatureC: value.minimumTemperatureC,
    optimumTemperatureC: value.optimumTemperatureC,
    maximumTemperatureC: value.maximumTemperatureC,
    requiredUnits: value.requiredUnits,
    minimumFactor: value.minimumFactor,
    appliesFromStageId: value.appliesFromStageId,
    appliesUntilStageId: value.appliesUntilStageId,
  }
}

const cloneThermalTime = (value) => {
  if (!value || typeof value !== 'object') {
    return null
  }

  if (['daily_min_max_mean_linear', 'daily_mean_linear'].includes(value.method)) {
    return {
      method: value.method,
      source: value.source === 'custom' ? 'custom' : 'suggested',
      baseTemperatureC: value.baseTemperatureC,
      optimumTemperatureC: value.optimumTemperatureC ?? null,
      maximumTemperatureC: value.maximumTemperatureC ?? null,
      stages: (value.stages || []).map((stage) => ({...stage})),
    }
  }

  return {
    source: value.source === 'custom' ? 'custom' : 'suggested',
    baseTemperatureC: value.baseTemperatureC,
    optimumTemperatureC: value.optimumTemperatureC,
    maximumTemperatureC: value.maximumTemperatureC,
    stages: Array.isArray(value.stages)
      ? value.stages.map((stage) => ({
          stageId: stage.stageId,
          thresholdGdd: stage.thresholdGdd,
        }))
      : [],
    photoperiod: clonePhotoperiod(value.photoperiod),
    vernalization: cloneVernalization(value.vernalization),
  }
}

const mergeThermalTimeWithSuggested = (value, suggested) => {
  const suggestedClone = cloneThermalTime(suggested)
  if (!suggestedClone) {
    return cloneThermalTime(value)
  }

  const currentClone = cloneThermalTime(value)
  if (!currentClone) {
    return suggestedClone
  }

  if (suggestedClone.method) return {...suggestedClone, ...currentClone}

  return {
    ...suggestedClone,
    ...currentClone,
    optimumTemperatureC: currentClone.optimumTemperatureC ?? suggestedClone.optimumTemperatureC,
    maximumTemperatureC: currentClone.maximumTemperatureC ?? suggestedClone.maximumTemperatureC,
    stages: currentClone.stages.length ? currentClone.stages : suggestedClone.stages,
    photoperiod:
      currentClone.photoperiod || suggestedClone.photoperiod
        ? {
            ...(suggestedClone.photoperiod || {}),
            ...(currentClone.photoperiod || {}),
          }
        : null,
    vernalization:
      currentClone.vernalization || suggestedClone.vernalization
        ? {
            ...(suggestedClone.vernalization || {}),
            ...(currentClone.vernalization || {}),
          }
        : null,
  }
}

const editableNumber = (value) => (value === '' ? '' : Number(value))
const editableFraction = (value) => (value === '' ? '' : Number(value) / 100)
const fractionAsPercent = (value) => (isFiniteNumber(value) ? Number(value) * 100 : '')

const ThermalNumberField = ({label, value, min, max, step, unit, disabled, onChange}) => (
  <label className='specific-copilot-agronomy-field'>
    <span>{label}</span>
    <div className='specific-copilot-thermal-time__input'>
      <input
        type='number'
        aria-label={label}
        className='form-control form-control-solid'
        min={min}
        max={max}
        step={step}
        value={value ?? ''}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
      <span>{unit}</span>
    </div>
  </label>
)

const getThermalTimeErrorKey = (thermalTime, expectedStageIds) => {
  if (
    !thermalTime ||
    thermalTime.baseTemperatureC === '' ||
    thermalTime.optimumTemperatureC === '' ||
    thermalTime.maximumTemperatureC === '' ||
    !thermalTime.stages?.length
  ) {
    return 'required'
  }

  const baseTemperature = Number(thermalTime.baseTemperatureC)
  const optimumTemperature = Number(thermalTime.optimumTemperatureC)
  const maximumTemperature = Number(thermalTime.maximumTemperatureC)
  if (!Number.isFinite(baseTemperature) || baseTemperature < -10 || baseTemperature > 30) {
    return 'base_temperature'
  }
  if (
    !Number.isFinite(optimumTemperature) ||
    !Number.isFinite(maximumTemperature) ||
    optimumTemperature <= baseTemperature ||
    maximumTemperature <= optimumTemperature ||
    maximumTemperature > 50
  ) {
    return 'cardinal_temperatures'
  }

  if (
    thermalTime.stages.length !== expectedStageIds.length ||
    thermalTime.stages.some((stage, index) => stage.stageId !== expectedStageIds[index])
  ) {
    return 'stages'
  }

  const thresholds = thermalTime.stages.map((stage) => Number(stage.thresholdGdd))
  if (
    thresholds.some(
      (threshold, index) =>
        thermalTime.stages[index].thresholdGdd === '' ||
        !Number.isFinite(threshold) ||
        threshold <= 0 ||
        threshold > 5000
    )
  ) {
    return 'threshold'
  }

  if (thresholds.some((threshold, index) => index > 0 && threshold <= thresholds[index - 1])) {
    return 'order'
  }

  const stageIds = thermalTime.stages.map((stage) => stage.stageId)
  const stageRangeValid = (modifier) => {
    const fromIndex = stageIds.indexOf(modifier.appliesFromStageId)
    const untilIndex = stageIds.indexOf(modifier.appliesUntilStageId)
    return fromIndex >= 0 && untilIndex > fromIndex
  }
  const photoperiod = thermalTime.photoperiod
  if (
    photoperiod &&
    (typeof photoperiod.enabled !== 'boolean' ||
      !['short_day', 'long_day'].includes(photoperiod.responseType) ||
      !isFiniteNumber(photoperiod.criticalHours) ||
      Number(photoperiod.criticalHours) < 8 ||
      Number(photoperiod.criticalHours) > 18 ||
      !isFiniteNumber(photoperiod.sensitivityPerHour) ||
      Number(photoperiod.sensitivityPerHour) < 0 ||
      Number(photoperiod.sensitivityPerHour) > 1 ||
      !isFiniteNumber(photoperiod.minimumFactor) ||
      Number(photoperiod.minimumFactor) < 0.1 ||
      Number(photoperiod.minimumFactor) > 1 ||
      !stageRangeValid(photoperiod))
  ) {
    return 'photoperiod'
  }

  const vernalization = thermalTime.vernalization
  if (
    vernalization &&
    (!isFiniteNumber(vernalization.minimumTemperatureC) ||
      !isFiniteNumber(vernalization.optimumTemperatureC) ||
      !isFiniteNumber(vernalization.maximumTemperatureC) ||
      Number(vernalization.minimumTemperatureC) < -10 ||
      Number(vernalization.minimumTemperatureC) >= Number(vernalization.optimumTemperatureC) ||
      Number(vernalization.optimumTemperatureC) >= Number(vernalization.maximumTemperatureC) ||
      Number(vernalization.maximumTemperatureC) > 50 ||
      !isFiniteNumber(vernalization.requiredUnits) ||
      Number(vernalization.requiredUnits) <= 0 ||
      Number(vernalization.requiredUnits) > 100 ||
      !isFiniteNumber(vernalization.minimumFactor) ||
      Number(vernalization.minimumFactor) < 0.1 ||
      Number(vernalization.minimumFactor) > 1 ||
      !stageRangeValid(vernalization))
  ) {
    return 'vernalization'
  }

  return ''
}

const formatNumber = (value, locale, digits = 1) => {
  if (!isFiniteNumber(value)) {
    return '-'
  }

  return new Intl.NumberFormat(locale === 'en' ? 'en-US' : 'es-AR', {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  }).format(Number(value))
}

const metric = (id, value, unit = '') => ({id, value, unit})

const formatProfileDate = (value) => {
  const parsedDate = parseISO(value || '')

  return isValid(parsedDate) ? format(parsedDate, 'dd-MM-yyyy') : value
}

const formatCalendarMonthDay = (value, locale) => {
  const [month, day] = String(value || '')
    .split('-')
    .map(Number)
  const parsedDate = new Date(2000, month - 1, day)
  if (!month || !day || parsedDate.getMonth() !== month - 1 || parsedDate.getDate() !== day) {
    return value || '-'
  }

  return new Intl.DateTimeFormat(locale === 'en' ? 'en-US' : 'es-AR', {
    day: 'numeric',
    month: 'long',
  }).format(parsedDate)
}

const formatChartDate = (value, locale) => {
  const numericValue =
    typeof value === 'number' || (typeof value === 'string' && /^\d{10,}$/.test(value))
      ? Number(value)
      : null
  const parsedDate = numericValue === null ? parseISO(value || '') : new Date(numericValue)

  if (!isValid(parsedDate)) {
    return value || '-'
  }

  return new Intl.DateTimeFormat(locale === 'en' ? 'en-US' : 'es-AR', {
    day: '2-digit',
    month: 'short',
  }).format(parsedDate)
}

const toPickerDate = (value) => {
  if (!value) {
    return null
  }

  const parsedDate = new Date(`${value}T12:00:00`)
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate
}

const toIsoDate = (value) =>
  value && !Number.isNaN(value.getTime()) ? format(value, 'yyyy-MM-dd') : ''

const chartBaseOptions = (categories, locale) => ({
  chart: {
    animations: {enabled: false},
    fontFamily: 'Poppins, sans-serif',
    toolbar: {show: false},
    zoom: {enabled: false},
  },
  dataLabels: {enabled: false},
  grid: {
    borderColor: '#e7eaf0',
    strokeDashArray: 3,
  },
  legend: {
    fontSize: '11px',
    labels: {colors: '#475569'},
    position: 'top',
    horizontalAlign: 'left',
  },
  noData: {text: '-'},
  stroke: {
    curve: 'smooth',
    lineCap: 'round',
  },
  tooltip: {
    x: {
      formatter: (_, {dataPointIndex}) =>
        new Intl.DateTimeFormat(locale === 'en' ? 'en-US' : 'es-AR', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        }).format(new Date(`${categories[dataPointIndex]}T12:00:00`)),
    },
  },
  xaxis: {
    categories,
    labels: {
      datetimeUTC: false,
      formatter: (value) => {
        const parsed = new Date(`${value}T12:00:00`)
        if (Number.isNaN(parsed.getTime())) {
          return value
        }
        return new Intl.DateTimeFormat(locale === 'en' ? 'en-US' : 'es-AR', {
          day: '2-digit',
          month: 'short',
        }).format(parsed)
      },
      hideOverlappingLabels: true,
      rotate: 0,
      style: {colors: '#7c849b', fontSize: '10px'},
    },
    tickAmount: 6,
    tooltip: {enabled: false},
  },
  yaxis: {
    labels: {
      formatter: (value) => formatNumber(value, locale, 0),
      style: {colors: '#7c849b', fontSize: '10px'},
    },
  },
  responsive: [
    {
      breakpoint: 640,
      options: {
        chart: {height: 220},
        legend: {
          fontSize: '10px',
          itemMargin: {horizontal: 5, vertical: 2},
        },
        xaxis: {
          tickAmount: 2,
          labels: {
            hideOverlappingLabels: true,
            rotate: 0,
            trim: false,
            style: {colors: '#7c849b', fontSize: '10px'},
          },
        },
        yaxis: {
          labels: {
            formatter: (value) => formatNumber(value, locale, 0),
            style: {colors: '#7c849b', fontSize: '9px'},
          },
        },
      },
    },
  ],
})

const AgronomyMetric = ({item}) => {
  const {t} = useTranslation('copilot')

  return (
    <article className='specific-copilot-agronomy-metric'>
      <span>{t(`agronomy.metrics.${item.id}`)}</span>
      <strong>
        {item.value}
        {item.unit && <small>{item.unit}</small>}
      </strong>
    </article>
  )
}

const PhenologyTimeline = ({currentStageId, items, temporalReference}) => {
  const {t, i18n} = useTranslation('copilot')
  const locale = i18n.language || 'es'
  const timeline = Array.isArray(items) ? items : []

  if (!timeline.length) {
    return null
  }

  const hasSeasonalEstimate = timeline.some((item) => item?.basis === 'seasonal_forecast')

  return (
    <section className='specific-copilot-phenology-timeline'>
      <div className='specific-copilot-phenology-timeline__heading'>
        <strong>{t('agronomy.timeline.title')}</strong>
        <span>{t('agronomy.timeline.subtitle')}</span>
      </div>
      <ol>
        {timeline.map((item) => {
          const isCurrent =
            item.stageId === currentStageId ||
            (currentStageId === 'pre_emergence' && item.stageId === 'sowing')
          const stateKey =
            item.status === 'reached' ? 'reached' : item.basis || item.status || 'forecast_horizon'

          return (
            <li
              key={item.stageId}
              className={[
                `specific-copilot-phenology-timeline__item specific-copilot-phenology-timeline__item--${stateKey}`,
                isCurrent ? 'is-current' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <span className='specific-copilot-phenology-timeline__dot' aria-hidden='true' />
              <div className='specific-copilot-phenology-timeline__stage'>
                <strong>{t(`agronomy.stages.${item.stageId || 'unknown'}`)}</strong>
                {isCurrent && (
                  <span>
                    {t(
                      `agronomy.timeline.${
                        temporalReference.isCurrent
                          ? 'current'
                          : temporalReference.referenceDate
                          ? 'at_cutoff'
                          : 'estimated'
                      }`
                    )}
                  </span>
                )}
              </div>
              <small className='specific-copilot-phenology-timeline__threshold'>
                {t('agronomy.timeline.threshold', {
                  value: formatNumber(item.thresholdGdd, locale, 0),
                })}
              </small>
              <time dateTime={item.date || undefined}>
                {item.date ? formatProfileDate(item.date) : '-'}
              </time>
              <small>{t(`agronomy.timeline.states.${stateKey}`)}</small>
            </li>
          )
        })}
      </ol>
      {hasSeasonalEstimate && (
        <p className='specific-copilot-phenology-timeline__seasonal-note'>
          {t('agronomy.timeline.seasonal_notice')}
        </p>
      )}
    </section>
  )
}

const AgronomyCharts = ({analysis, recentNdvi, recentEvi}) => {
  const {t, i18n} = useTranslation('copilot')
  const [waterView, setWaterView] = useState('daily')
  const locale = i18n.language || 'es'
  const daily = analysis?.climate?.daily || []
  const ndvi = recentNdvi?.observations || []
  const evi = recentEvi?.observations || []
  const vegetationIndices = buildVegetationIndexChartData(ndvi, evi)
  const categories = daily.map((item) => item.date)
  const baseOptions = chartBaseOptions(categories, locale)
  const cumulativeWaterData = buildCumulativeWaterData(daily)
  const accumulatedDeficit = summarizeAccumulatedClimaticDeficit(cumulativeWaterData)
  const showCumulativeWater = waterView === 'cumulative'
  const partialVegetationCatalog = [recentNdvi, recentEvi].some((index) => index?.catalogCoverage?.status === 'partial')

  const gddOptions = {
    ...baseOptions,
    colors: ['#009ef7'],
    fill: {opacity: 0.08, type: 'solid'},
    stroke: {...baseOptions.stroke, width: 3},
    yaxis: {
      ...baseOptions.yaxis,
      title: {text: 'GDD', style: {fontSize: '10px', color: '#7c849b'}},
    },
  }
  const waterOptions = {
    ...baseOptions,
    colors: showCumulativeWater ? ['#14b8a6', '#f59e0b', '#ef4444'] : ['#14b8a6', '#f59e0b'],
    fill: showCumulativeWater
      ? {opacity: [1, 1, 0.14], type: 'solid'}
      : {opacity: 1, type: 'solid'},
    markers: {size: 0},
    plotOptions: {
      bar: {
        borderRadius: showCumulativeWater ? 0 : 2,
        columnWidth: '65%',
      },
    },
    stroke: {
      ...baseOptions.stroke,
      dashArray: showCumulativeWater ? [0, 0, 5] : [0, 0],
      width: showCumulativeWater ? [3, 3, 2] : [0, 2],
    },
    tooltip: {
      ...baseOptions.tooltip,
      shared: true,
      y: {
        formatter: (value) => `${formatNumber(value, locale, 1)} mm`,
      },
    },
    yaxis: {
      ...baseOptions.yaxis,
      decimalsInFloat: 0,
      labels: {
        formatter: (value) => formatNumber(value, locale, 0),
      },
      title: {text: 'mm', style: {fontSize: '10px', color: '#7c849b'}},
    },
  }
  const temperatureOptions = {
    ...baseOptions,
    colors: ['#2563eb', '#ef4444'],
    stroke: {...baseOptions.stroke, width: [2, 2]},
    yaxis: {
      ...baseOptions.yaxis,
      title: {text: '°C', style: {fontSize: '10px', color: '#7c849b'}},
    },
  }
  const vegetationBaseOptions = chartBaseOptions([], locale)
  const vegetationIndexValues = [...vegetationIndices.ndvi, ...vegetationIndices.evi].map(
    (point) => point.y
  )
  const vegetationIndexOptions = {
    ...vegetationBaseOptions,
    colors: ['#16a34a', '#0ea5e9'],
    markers: {size: 4, strokeWidth: 0},
    stroke: {curve: 'straight', lineCap: 'round', width: [2, 2]},
    tooltip: {
      ...vegetationBaseOptions.tooltip,
      shared: true,
      intersect: false,
      x: {
        formatter: (value) => formatChartDate(String(value), locale),
      },
      y: {
        formatter: (value) =>
          value === null || value === undefined ? '-' : formatNumber(value, locale, 2),
      },
    },
    xaxis: {
      ...vegetationBaseOptions.xaxis,
      categories: [],
      type: 'datetime',
      labels: {
        ...vegetationBaseOptions.xaxis.labels,
        formatter: (value) => formatChartDate(value, locale),
      },
    },
    yaxis: {
      min: vegetationIndexValues.some((value) => value < 0) ? -1 : 0,
      max: 1,
      tickAmount: vegetationIndexValues.some((value) => value < 0) ? 4 : 5,
      labels: {
        formatter: (value) => formatNumber(value, locale, 2),
        style: {colors: '#7c849b', fontSize: '10px'},
      },
    },
  }

  return (
    <div className='specific-copilot-agronomy-charts'>
      {partialVegetationCatalog && <div className='specific-copilot-agronomy-calendar-notice' style={{gridColumn: '1 / -1'}} role='status'>
        {t('agronomy.catalog_partial_notice')}
      </div>}
      {(!isObservationalCrop(analysis?.profile?.crop) || isThermalOnlyAgronomicProfile(analysis?.profile)) && <article
        className='specific-copilot-chart'
        data-pdf-visual-kind='chart'
        data-pdf-visual-id='agronomy-gdd'
        data-pdf-visual-state='pending'
      >
        <h5>{t('agronomy.charts.gdd')}</h5>
        <Chart
          type='area'
          height={230}
          options={withPdfChartReadiness(gddOptions)}
          series={[
            {
              name: t('agronomy.series.cumulative_gdd'),
              data: daily.map((item) => item.thermalTemperatureMissing ||
                (isThermalOnlyAgronomicProfile(analysis?.profile) && item.climateObservationAvailable === false)
                ? null : item.cumulativeGdd),
            },
          ]}
        />
      </article>}

      <article
        className='specific-copilot-chart'
        data-pdf-chart-variants='true'
        data-pdf-visual-kind='chart'
        data-pdf-visual-id={`agronomy-water-${waterView}`}
        data-pdf-visual-state='pending'
      >
        <div className='specific-copilot-chart__heading'>
          <h5>{t('agronomy.charts.water')}</h5>
          <div
            className='specific-copilot-chart__mode'
            role='group'
            aria-label={t('agronomy.charts.water_view_label')}
          >
            {['daily', 'cumulative'].map((view) => (
              <button
                key={view}
                type='button'
                data-pdf-chart-mode={view}
                className={waterView === view ? 'is-active' : ''}
                aria-pressed={waterView === view}
                onClick={() => setWaterView(view)}
              >
                {t(`agronomy.charts.water_views.${view}`)}
              </button>
            ))}
          </div>
        </div>
        <Chart
          key={waterView}
          type='line'
          height={230}
          options={withPdfChartReadiness(waterOptions)}
          series={
            showCumulativeWater
              ? [
                  {
                    name: t('agronomy.series.cumulative_precipitation'),
                    type: 'line',
                    data: cumulativeWaterData.map((item) => item.cumulativePrecipitationMm),
                  },
                  {
                    name: t('agronomy.series.cumulative_reference_et0'),
                    type: 'line',
                    data: cumulativeWaterData.map((item) => item.cumulativeReferenceEt0Mm),
                  },
                  {
                    name: t('agronomy.series.accumulated_climatic_deficit'),
                    type: 'area',
                    data: cumulativeWaterData.map((item) => item.accumulatedClimaticDeficitMm),
                  },
                ]
              : [
                  {
                    name: t('agronomy.series.precipitation'),
                    type: 'column',
                    data: daily.map((item) => item.precipitationMm),
                  },
                  {
                    name: t('agronomy.series.reference_et0'),
                    type: 'line',
                    data: daily.map((item) => item.referenceEt0Mm),
                  },
                ]
          }
        />
        {showCumulativeWater && cumulativeWaterData.length > 0 && (
          <p
            className={`specific-copilot-chart__balance ${
              accumulatedDeficit.hasDeficit
                ? 'specific-copilot-chart__balance--deficit'
                : 'specific-copilot-chart__balance--balanced'
            }`}
          >
            {accumulatedDeficit.hasDeficit
              ? t('agronomy.charts.accumulated_deficit_detected', {
                  closing: formatNumber(accumulatedDeficit.closingDeficitMm, locale, 1),
                  date: formatChartDate(accumulatedDeficit.peakDate, locale),
                  peak: formatNumber(accumulatedDeficit.peakDeficitMm, locale, 1),
                })
              : t('agronomy.charts.accumulated_deficit_not_detected')}
          </p>
        )}
      </article>

      <article
        className='specific-copilot-chart'
        data-pdf-visual-kind='chart'
        data-pdf-visual-id='agronomy-temperature'
        data-pdf-visual-state='pending'
      >
        <h5>{t('agronomy.charts.temperature')}</h5>
        <Chart
          type='line'
          height={230}
          options={withPdfChartReadiness(temperatureOptions)}
          series={[
            {
              name: t('agronomy.series.minimum_temperature'),
              data: daily.map((item) => item.minTemperatureC),
            },
            {
              name: t('agronomy.series.maximum_temperature'),
              data: daily.map((item) => item.maxTemperatureC),
            },
          ]}
        />
      </article>

      <article
        className='specific-copilot-chart'
        data-pdf-visual-kind='chart'
        data-pdf-visual-id='agronomy-vegetation-indices'
        data-pdf-visual-state={
          vegetationIndices.ndvi.length > 0 || vegetationIndices.evi.length > 0
            ? 'pending'
            : 'unavailable'
        }
      >
        <h5>{t('agronomy.charts.vegetation_indices')}</h5>
        {vegetationIndices.ndvi.length > 0 || vegetationIndices.evi.length > 0 ? (
          <Chart
            type='line'
            height={230}
            options={withPdfChartReadiness(vegetationIndexOptions)}
            series={[
              {
                name: t('agronomy.series.mean_ndvi'),
                data: vegetationIndices.ndvi,
              },
              {
                name: t('agronomy.series.mean_evi'),
                data: vegetationIndices.evi,
              },
            ]}
          />
        ) : (
          <div className='specific-copilot-chart__empty'>
            {t('agronomy.vegetation_indices_empty')}
          </div>
        )}
      </article>
    </div>
  )
}

const FieldObservationTimeline = ({profile, items, locale, t}) => {
  const stageIds = getAgronomicFieldObservationStageIds(profile.crop)
  const observations = (items || []).filter((item) =>
    item.basis === 'field_observation' && item.status === 'observed' && stageIds.includes(item.stageId)
  )
  return (
    <section className='specific-copilot-field-observations' aria-label={t('agronomy.field_observations.title')}>
      <strong>{t('agronomy.field_observations.title')}</strong>
      <p>{t('agronomy.field_observations.intro')}</p>
      {observations.length ? <ul>
        {observations.map((item) => <li key={item.stageId}>
          <strong>{t(`agronomy.field_observations.stages.${profile.crop}.${item.stageId}`)}</strong>
          <span>{formatProfileDate(item.date)}</span>
          <span>{t('agronomy.field_observations.accumulated', {value: formatNumber(item.accumulatedGdd, locale, 1)})}</span>
          {item.thermalAccumulationComplete === false && <small>
            {t('agronomy.thermal_reference.partial_notice', {count: item.missingTemperatureDays || 0})}
          </small>}
        </li>)}
      </ul> : <p>{t('agronomy.field_observations.empty')}</p>}
    </section>
  )
}

const AgronomyResult = ({analysis, recentNdvi, recentEvi, onEdit}) => {
  const {t, i18n} = useTranslation('copilot')
  const locale = i18n.language || 'es'
  const summary = analysis.climate?.summary || {}
  const phenology = analysis.phenology || {}
  const temporalReference = getPhenologyTemporalReference(analysis)
  const profile = analysis.profile || {}
  const observational = isObservationalCrop(profile.crop)
  const thermalOnly = isThermalOnlyAgronomicProfile(profile)
  const sowingCalendarProvenance = analysis.sowingCalendar?.provenance
  const thermalModelProvenance = phenology.provenance || profile.thermalTime?.provenance
  const developmentModifiers = phenology.developmentModifiers || {}
  const metrics = [
    ...(thermalOnly ? [metric('gdd', formatNumber(phenology.accumulatedGdd ?? summary.growingDegreeDays, locale, 0), ' GDD')] : []),
    ...(!observational ? [
      metric('gdd', formatNumber(phenology.accumulatedGdd, locale, 0), ' GDD'),
      metric('gdd_to_next', formatNumber(phenology.gddToNextStage, locale, 0), ' GDD'),
    ] : []),
    metric('precipitation', formatNumber(summary.precipitationMm, locale, 1), ' mm'),
    metric('reference_et0', formatNumber(summary.referenceEt0Mm, locale, 1), ' mm'),
    metric('climatic_balance', formatNumber(summary.climaticBalanceMm, locale, 1), ' mm'),
    metric('mean_temperature', formatNumber(summary.meanTemperatureC, locale, 1), ' °C'),
    metric('minimum_temperature', formatNumber(summary.minimumTemperatureC, locale, 1), ' °C'),
    metric('maximum_temperature', formatNumber(summary.maximumTemperatureC, locale, 1), ' °C'),
    metric('hot_days', formatNumber(summary.hotDays, locale, 0)),
    metric('frost_days', formatNumber(summary.frostDays, locale, 0)),
    metric('dry_spell', formatNumber(summary.longestDrySpellDays, locale, 0), ' d'),
  ]

  return (
    <section className='specific-copilot-agronomy-result'>
      <div className='specific-copilot-agronomy-result__header'>
        <div>
          <span>{t(`agronomy.crops.${profile.crop}`, {defaultValue: getCropLabel(profile.crop, locale)})}</span>
          {!observational && <strong>{profile.genotype}</strong>}
          {thermalOnly && <strong>{profile.genotype || t(`agronomy.thermal_reference.reference_${profile.crop}`)}</strong>}
          {thermalOnly && profile.cultivarName && <small>{t('agronomy.thermal_reference.cultivar_label')}: {profile.cultivarName}</small>}
          <small>
            {t(thermalOnly ? `agronomy.thermal_reference.start_${profile.crop}` : observational ? 'agronomy.observational.start_short' : 'agronomy.sowing_date_short')}: {formatProfileDate(profile.sowingDate)}
          </small>
          {profile.harvestDate && (
            <small>
              {t(observational ? 'agronomy.observational.end_short' : 'agronomy.harvest_date_short')}: {formatProfileDate(profile.harvestDate)}
            </small>
          )}
          {!observational && <small>
            {t('agronomy.thermal_time.result_summary', {
              base: formatNumber(phenology.baseTemperatureC, locale, 1),
              optimum: formatNumber(phenology.optimumTemperatureC, locale, 1),
              maximum: formatNumber(phenology.maximumTemperatureC, locale, 1),
              source: t(
                `agronomy.thermal_time.sources.${phenology.thermalTimeSource || 'suggested'}`
              ),
            })}
          </small>}
          {thermalOnly && <small>{t('agronomy.thermal_reference.base_summary', {
            base: formatNumber(profile.thermalTime?.baseTemperatureC, locale, 1),
            source: t(`agronomy.thermal_time.sources.${profile.thermalTime?.source || 'suggested'}`),
          })}</small>}
        </div>
        <button type='button' className='btn btn-sm btn-light-primary' onClick={onEdit}>
          {t('agronomy.edit_profile')}
        </button>
      </div>

      {observational && !thermalOnly && <div className='specific-copilot-agronomy-calendar-notice' role='status'>
        {t('agronomy.observational.notice')}
      </div>}

      {thermalOnly && <>
        <div className='specific-copilot-agronomy-calendar-notice specific-copilot-thermal-reference-notice' role='status'>
          <span>{t('agronomy.thermal_reference.notice')}</span>
          <small>{t(`agronomy.thermal_reference.scope_${profile.crop}`)}</small>
          {profile.thermalTime?.source === 'custom' && <small>{t('agronomy.thermal_reference.custom_notice')}</small>}
        </div>
        {summary.thermalAccumulationComplete === false && <div
          className='specific-copilot-agronomy-calendar-notice specific-copilot-agronomy-calendar-notice--warning specific-copilot-thermal-reference-notice' role='status'>
          {t('agronomy.thermal_reference.partial_notice', {count: summary.missingTemperatureDays || 0})}
        </div>}
        <FieldObservationTimeline profile={profile} items={phenology.timeline} locale={locale} t={t} />
      </>}

      {!observational && analysis.sowingCalendar?.selectionStatus === 'tolerance' && (
        <div
          className='specific-copilot-agronomy-calendar-notice specific-copilot-agronomy-calendar-notice--warning'
          role='status'
        >
          {t('agronomy.sowing_calendar.tolerance_warning', {
            crop: t(`agronomy.crops.${profile.crop}`),
          })}
        </div>
      )}

      {!observational && <article className='specific-copilot-phenology'>
        <div className='specific-copilot-phenology__heading'>
          <div>
            <span>
              {temporalReference.referenceDate
                ? t('agronomy.phenology_title_at', {
                    date: formatProfileDate(temporalReference.referenceDate),
                  })
                : t('agronomy.phenology_title')}
            </span>
            <strong>{t(`agronomy.stages.${phenology.stageId || 'unknown'}`)}</strong>
          </div>
          <span
            className='badge badge-light-warning'
            title={t('agronomy.confidence_basis')}
            aria-label={t('agronomy.confidence_basis')}
          >
            {t(`agronomy.confidence.${phenology.confidence || 'low'}`)}
          </span>
        </div>
        <div className='specific-copilot-phenology__track' aria-hidden='true'>
          <span style={{width: `${Math.min(Math.max(phenology.progressPercent || 0, 0), 100)}%`}} />
        </div>
        <p>{t('agronomy.phenology_notice')}</p>
        {temporalReference.closed && <p>{t('agronomy.phenology_closed_notice')}</p>}
        {!temporalReference.referenceDate && <p>{t('agronomy.phenology_date_unknown')}</p>}
        <p>{t('agronomy.confidence_basis')}</p>
        <PhenologyTimeline
          currentStageId={phenology.stageId}
          items={phenology.timeline}
          temporalReference={temporalReference}
        />
      </article>}

      <div className='specific-copilot-agronomy-metrics'>
        {metrics.map((item) => (
          <AgronomyMetric key={item.id} item={item} />
        ))}
      </div>

      {analysis.periodPolicy?.cappedAutomatically && (
        <p className='specific-copilot-agronomy-source'>
          {t(observational ? 'agronomy.observational.period_capped' : 'agronomy.analysis_period_capped', {
            date: formatProfileDate(analysis.periodPolicy.analysisEndDate),
          })}
        </p>
      )}
      {!observational && (sowingCalendarProvenance?.modelVersion || thermalModelProvenance?.modelVersion) && (
        <div className='specific-copilot-agronomy-traceability'>
          {sowingCalendarProvenance?.modelVersion && (
            <p>
              {t('agronomy.sowing_calendar.methodology')}
            </p>
          )}
          {thermalModelProvenance?.modelVersion && (
            <>
              <p>
                {t('agronomy.thermal_time.methodology')}
              </p>
              <p>
                {t(
                  `agronomy.thermal_time.scope_${
                    phenology.thermalTimeSource === 'custom' ? 'custom' : 'suggested'
                  }`
                )}
              </p>
              {developmentModifiers.photoperiod?.enabled && (
                <p>
                  {developmentModifiers.photoperiod.appliedDays > 0
                    ? t('agronomy.thermal_time.photoperiod.result', {
                        days: developmentModifiers.photoperiod.appliedDays,
                        factor: formatNumber(
                          developmentModifiers.photoperiod.meanFactor,
                          locale,
                          2
                        ),
                      })
                    : t('agronomy.thermal_time.photoperiod.not_applied')}
                </p>
              )}
              {developmentModifiers.vernalization?.enabled && (
                <p>
                  {developmentModifiers.vernalization.appliedDays > 0
                    ? t('agronomy.thermal_time.vernalization.result', {
                        completion: formatNumber(
                          developmentModifiers.vernalization.completionPercent,
                          locale,
                          0
                        ),
                        accumulated: formatNumber(
                          developmentModifiers.vernalization.accumulatedUnits,
                          locale,
                          1
                        ),
                        required: formatNumber(
                          developmentModifiers.vernalization.requiredUnits,
                          locale,
                          1
                        ),
                      })
                    : t('agronomy.thermal_time.vernalization.not_applied')}
                </p>
              )}
            </>
          )}
        </div>
      )}
      <p className='specific-copilot-agronomy-source'>
        {t('agronomy.climate_source_notice', {
          source: analysis.climate?.source?.name || 'Open-Meteo',
          completeness: formatNumber(thermalOnly ? summary.thermalCompletenessPercent : summary.completenessPercent, locale, 0),
        })}
      </p>
      <p className='specific-copilot-agronomy-source'>{t('agronomy.climatic_balance_notice')}</p>

      <AgronomyCharts analysis={analysis} recentNdvi={recentNdvi} recentEvi={recentEvi} />
    </section>
  )
}

const AgronomyQuestionnaire = ({
  catalog,
  profile,
  analyzedProfile,
  onChange,
  onSubmit,
  loading,
}) => {
  const {t, i18n} = useTranslation('copilot')
  const locale = i18n.language || 'es'
  const [openedDatePicker, setOpenedDatePicker] = useState(null)
  const observational = isObservationalCrop(profile.crop)
  const thermalOnly = isThermalOnlyAgronomicProfile(profile)
  const referenceCatalogProfile = (catalog || []).find((item) =>
    item.crop === profile.crop && item.id === THERMAL_REFERENCE_PROFILES[profile.crop]?.id && item.profileMode === 'thermal_only'
  )
  const fieldObservations = Array.isArray(profile.fieldObservations) ? profile.fieldObservations : []
  const observationStageIds = getAgronomicFieldObservationStageIds(profile.crop)
  const availableNewStageIds = observationStageIds.filter((id, index) =>
    index > observationStageIds.indexOf(fieldObservations[fieldObservations.length - 1]?.stageId)
  )
  const maximumAnalysisMonths = getCropAnalysisMonths(profile.crop)
  const cropOptions = AGRONOMIC_CROP_IDS.map((crop) => ({
    value: crop,
    label: t(`agronomy.crops.${crop}`),
  }))
  const selectedCropCatalogProfile = (catalog || []).find((item) => item.crop === profile.crop)
  const sowingCalendar = selectedCropCatalogProfile?.sowingCalendar
  const sowingDateStatus = getAgronomicSowingDateStatus(profile.sowingDate, sowingCalendar)
  const genotypeOptions = (catalog || [])
    .filter((item) => item.crop === profile.crop)
    .map((item) => ({
      value: item.id,
      label: item.label,
    }))
  const selectedCatalogProfile = (catalog || []).find((item) => item.id === profile.genotypeId)
  const suggestedThermalTime = selectedCatalogProfile?.thermalTime || null
  const expectedStageIds = Array.isArray(suggestedThermalTime?.stages)
    ? suggestedThermalTime.stages.map((stage) => stage.stageId)
    : []
  const thermalTimeErrorKey = profile.genotypeId
    ? getThermalTimeErrorKey(profile.thermalTime, expectedStageIds)
    : ''
  const thermalTimeValid = hasValidThermalTime(profile.thermalTime, expectedStageIds)
  const thermalOnlyTimeValid = hasValidThermalOnlyTime(profile.thermalTime, profile.crop)
  const fieldObservationsValid = hasValidAgronomicFieldObservations(profile)
  const analysisPeriodValid = hasValidAgronomicAnalysisPeriod(profile)
  const complete = Boolean(
    profile.crop &&
      profile.genotypeId &&
      profile.sowingDate &&
      (thermalOnly ? thermalOnlyTimeValid && fieldObservationsValid && (profile.cultivarName || '').trim().length <= 120 : observational || thermalTimeValid) &&
      analysisPeriodValid &&
      (observational || sowingDateStatus !== 'outside')
  )
  const hasAnalyzedProfile = hasCompleteAgronomicProfile(analyzedProfile)
  const hasChanges = !hasAnalyzedProfile || !areAgronomicProfilesEqual(profile, analyzedProfile)
  const genotypeLabel =
    profile.crop === 'maize' ? t('agronomy.hybrid_label') : t('agronomy.variety_label')
  const sowingDate = toPickerDate(profile.sowingDate)
  const harvestDate = toPickerDate(profile.harvestDate)
  const dateDisabled = !profile.crop || loading
  const harvestDateDisabled = dateDisabled || !profile.sowingDate
  const todayIso = thermalOnly ? getAgronomicToday() : format(new Date(), 'yyyy-MM-dd')
  const maximumAnalysisEndDate = getMaximumAgronomicAnalysisEndDate(profile.sowingDate, maximumAnalysisMonths)
  const maximumSelectableHarvestDate = [todayIso, maximumAnalysisEndDate].filter(Boolean).sort()[0]

  useEffect(() => {
    if (!profile.genotypeId || !suggestedThermalTime) {
      return
    }

    const mergedThermalTime = mergeThermalTimeWithSuggested(
      profile.thermalTime,
      suggestedThermalTime
    )
    if (
      JSON.stringify(cloneThermalTime(profile.thermalTime)) === JSON.stringify(mergedThermalTime)
    ) {
      return
    }

    onChange((currentProfile) => {
      if (currentProfile.genotypeId !== profile.genotypeId) {
        return currentProfile
      }

      return {
        ...currentProfile,
        thermalTime: mergeThermalTimeWithSuggested(
          currentProfile.thermalTime,
          suggestedThermalTime
        ),
      }
    })
  }, [onChange, profile.genotypeId, profile.thermalTime, suggestedThermalTime])

  const updateThermalTime = (updater) => {
    onChange((currentProfile) => {
      const currentThermalTime = cloneThermalTime(currentProfile.thermalTime)
      if (!currentThermalTime) {
        return currentProfile
      }

      return {
        ...currentProfile,
        thermalTime: {
          ...updater(currentThermalTime),
          source: 'custom',
        },
      }
    })
  }

  return (
    <section className='specific-copilot-agronomy-questionnaire'>
      <div className='specific-copilot-agronomy-questionnaire__intro'>
        <div>
          <strong>{t('agronomy.questionnaire_title')}</strong>
          <p>{t(thermalOnly ? 'agronomy.thermal_reference.intro' : observational ? 'agronomy.observational.intro' : 'agronomy.questionnaire_intro')}</p>
        </div>
      </div>

      <form
        onSubmit={(event) => {
          event.preventDefault()
          if (complete && !loading) {
            onSubmit(profile)
          }
        }}
      >
        <div className='specific-copilot-agronomy-form'>
          <AppSelect
            label={t('agronomy.crop_label')}
            placeholder={t('agronomy.crop_placeholder')}
            options={cropOptions}
            value={profile.crop}
            onChange={(crop) =>
              onChange((currentProfile) => {
                const nextCalendar = (catalog || []).find(
                  (item) => item.crop === crop
                )?.sowingCalendar
                const nextDateStatus = getAgronomicSowingDateStatus(
                  currentProfile.sowingDate,
                  nextCalendar
                )
                const nextObservational = isObservationalCrop(crop)
                const keepCurrentDates = nextObservational || !currentProfile.sowingDate || nextDateStatus !== 'outside'
                const nextMaximum = getMaximumAgronomicAnalysisEndDate(currentProfile.sowingDate, getCropAnalysisMonths(crop))
                const legacyProfile = {...currentProfile}
                delete legacyProfile.profileMode
                delete legacyProfile.fieldObservations
                delete legacyProfile.cultivarName

                return {
                  ...legacyProfile,
                  crop,
                  genotypeId: nextObservational ? getObservationalProfileId(crop) : '',
                  sowingDate: keepCurrentDates ? currentProfile.sowingDate : '',
                  harvestDate: keepCurrentDates && (!currentProfile.harvestDate || currentProfile.harvestDate <= nextMaximum) ? currentProfile.harvestDate : '',
                  thermalTime: null,
                  ...(nextObservational ? {profileMode: 'observational'} : {}),
                }
              })
            }
            disabled={loading}
          />

          {referenceCatalogProfile && <AppSelect
            label={t('agronomy.thermal_reference.mode_label')}
            options={[
              {value: getObservationalProfileId(profile.crop), label: t('agronomy.thermal_reference.observational_option')},
              {value: referenceCatalogProfile.id, label: t(`agronomy.thermal_reference.reference_${profile.crop}`)},
            ]}
            value={profile.genotypeId}
            disabled={loading}
            onChange={(genotypeId) => onChange((currentProfile) => {
              const nextProfile = {...currentProfile, genotypeId}
              if (genotypeId === referenceCatalogProfile.id) {
                return {...nextProfile, profileMode: 'thermal_only', thermalTime: cloneThermalTime(referenceCatalogProfile.thermalTime)}
              }
              delete nextProfile.fieldObservations
              delete nextProfile.cultivarName
              return {...nextProfile, profileMode: 'observational', thermalTime: null}
            })}
          />}

          {thermalOnly && <label className='specific-copilot-agronomy-field'>
            <span>{t('agronomy.thermal_reference.cultivar_label')}</span>
            <input type='text' className='form-control form-control-solid' maxLength={120}
              value={profile.cultivarName || ''} disabled={loading}
              placeholder={t('agronomy.thermal_reference.cultivar_placeholder')}
              onChange={(event) => onChange((currentProfile) => ({...currentProfile, cultivarName: event.target.value}))} />
          </label>}

          {!observational && <AppSelect
            label={genotypeLabel}
            placeholder={t('agronomy.genotype_placeholder')}
            options={genotypeOptions}
            value={profile.genotypeId}
            onChange={(genotypeId) => {
              const selectedProfile = (catalog || []).find((item) => item.id === genotypeId)
              onChange((currentProfile) => ({
                ...currentProfile,
                genotypeId,
                thermalTime: cloneThermalTime(selectedProfile?.thermalTime),
              }))
            }}
            disabled={!profile.crop || loading}
          />}

          <div className='specific-copilot-agronomy-field'>
            <span>{t(thermalOnly ? `agronomy.thermal_reference.start_${profile.crop}` : observational ? 'agronomy.observational.start_label' : 'agronomy.sowing_date_label')}</span>
            <button
              type='button'
              className='specific-copilot-date-trigger'
              aria-haspopup='dialog'
              aria-expanded={openedDatePicker === 'sowing'}
              disabled={dateDisabled}
              onClick={() => setOpenedDatePicker('sowing')}
            >
              <span className={profile.sowingDate ? '' : 'is-placeholder'}>
                {sowingDate
                  ? format(sowingDate, 'dd-MM-yyyy')
                  : t('agronomy.sowing_date_placeholder')}
              </span>
              <KTSVG
                path='/media/icons/duotune/general/gen014.svg'
                className='specific-copilot-date-trigger__icon'
              />
            </button>
            <DatePicker
              open={!dateDisabled && openedDatePicker === 'sowing'}
              value={sowingDate}
              autoOk
              okLabel={null}
              cancelLabel={t('common:actions.cancel')}
              onOpen={() => {
                if (!dateDisabled) {
                  setOpenedDatePicker('sowing')
                }
              }}
              onClose={() => setOpenedDatePicker(null)}
              onChange={(date) => {
                const nextSowingDate = toIsoDate(date)
                if (!nextSowingDate) {
                  return
                }
                const maximumEndDate = getMaximumAgronomicAnalysisEndDate(nextSowingDate, maximumAnalysisMonths)

                onChange((currentProfile) => ({
                  ...currentProfile,
                  sowingDate: nextSowingDate,
                  harvestDate:
                    currentProfile.harvestDate &&
                    (currentProfile.harvestDate < nextSowingDate ||
                      currentProfile.harvestDate > maximumEndDate)
                      ? ''
                      : currentProfile.harvestDate,
                }))
                setOpenedDatePicker(null)
              }}
              shouldDisableDate={(date) => {
                const candidate = toIsoDate(date)
                return (
                  !candidate ||
                  candidate > todayIso ||
                  Boolean(profile.harvestDate && candidate > profile.harvestDate) ||
                  (!observational && getAgronomicSowingDateStatus(candidate, sowingCalendar) === 'outside')
                )
              }}
              animateYearScrolling
              DialogProps={{
                container: () => document.querySelector('.specific-copilot-modal') || document.body,
                maxWidth: false,
                PaperProps: {
                  className: 'view-land-date-picker-dialog',
                },
              }}
              TextFieldComponent={() => null}
            />
          </div>

          <div className='specific-copilot-agronomy-field'>
            <span>{t(observational ? 'agronomy.observational.end_label' : 'agronomy.harvest_date_label')}</span>
            <button
              type='button'
              className='specific-copilot-date-trigger'
              aria-haspopup='dialog'
              aria-expanded={openedDatePicker === 'harvest'}
              disabled={harvestDateDisabled}
              onClick={() => setOpenedDatePicker('harvest')}
            >
              <span className={profile.harvestDate ? '' : 'is-placeholder'}>
                {harvestDate
                  ? format(harvestDate, 'dd-MM-yyyy')
                  : t('agronomy.harvest_date_placeholder')}
              </span>
              <KTSVG
                path='/media/icons/duotune/general/gen014.svg'
                className='specific-copilot-date-trigger__icon'
              />
            </button>
            <DatePicker
              open={!harvestDateDisabled && openedDatePicker === 'harvest'}
              value={harvestDate}
              maxDate={toPickerDate(maximumSelectableHarvestDate)}
              autoOk
              clearable
              okLabel={null}
              clearLabel={t(observational ? 'agronomy.observational.clear_end' : 'agronomy.clear_harvest_date')}
              cancelLabel={t('common:actions.cancel')}
              onOpen={() => {
                if (!harvestDateDisabled) {
                  setOpenedDatePicker('harvest')
                }
              }}
              onClose={() => setOpenedDatePicker(null)}
              onChange={(date) => {
                const nextHarvestDate = toIsoDate(date)
                onChange((currentProfile) => ({
                  ...currentProfile,
                  harvestDate: nextHarvestDate,
                }))
                setOpenedDatePicker(null)
              }}
              shouldDisableDate={(date) => {
                const candidate = toIsoDate(date)
                return (
                  !candidate ||
                  candidate > todayIso ||
                  Boolean(profile.sowingDate && candidate < profile.sowingDate) ||
                  Boolean(maximumAnalysisEndDate && candidate > maximumAnalysisEndDate)
                )
              }}
              animateYearScrolling
              DialogProps={{
                container: () => document.querySelector('.specific-copilot-modal') || document.body,
                maxWidth: false,
                PaperProps: {
                  className: 'view-land-date-picker-dialog',
                },
              }}
              TextFieldComponent={() => null}
            />
          </div>
        </div>

        {thermalOnly && <div className='specific-copilot-agronomy-calendar-notice specific-copilot-thermal-reference-notice' role='status'>
          <span>{t(`agronomy.thermal_reference.scope_${profile.crop}`)}</span>
          <small>{t('agronomy.thermal_reference.dates_notice')}</small>
        </div>}

        {!observational && sowingCalendar?.status === 'available' && (
          <div
            className={`specific-copilot-agronomy-calendar-notice${
              sowingDateStatus === 'tolerance'
                ? ' specific-copilot-agronomy-calendar-notice--warning'
                : sowingDateStatus === 'outside'
                ? ' specific-copilot-agronomy-calendar-notice--error'
                : ''
            }`}
            role={sowingDateStatus === 'outside' ? 'alert' : 'status'}
          >
            <span>
              {sowingDateStatus === 'tolerance'
                ? t('agronomy.sowing_calendar.tolerance_warning', {
                    crop: t(`agronomy.crops.${profile.crop}`),
                  })
                : sowingDateStatus === 'outside'
                ? t('agronomy.sowing_calendar.outside_error', {
                    crop: t(`agronomy.crops.${profile.crop}`),
                    start: formatCalendarMonthDay(sowingCalendar.selectableDateFrom, locale),
                    end: formatCalendarMonthDay(sowingCalendar.selectableDateTo, locale),
                  })
                : t('agronomy.sowing_calendar.usual_window', {
                    crop: t(`agronomy.crops.${profile.crop}`),
                    start: formatCalendarMonthDay(sowingCalendar.usualDateFrom, locale),
                    end: formatCalendarMonthDay(sowingCalendar.usualDateTo, locale),
                  })}
            </span>
            {sowingCalendar.provenance?.modelVersion && (
              <small>
                {t('agronomy.sowing_calendar.methodology')}
              </small>
            )}
          </div>
        )}

        {profile.sowingDate && (
          <p className='specific-copilot-agronomy-form__period-notice'>
            {t(observational ? 'agronomy.observational.period_notice' : 'agronomy.analysis_period_notice', {
              months: maximumAnalysisMonths,
              date: maximumAnalysisEndDate
                ? format(toPickerDate(maximumAnalysisEndDate), 'dd-MM-yyyy')
                : '',
            })}
          </p>
        )}

        {thermalOnly && <>
          <section className='specific-copilot-thermal-time'>
            <div className='specific-copilot-thermal-time__heading'>
              <strong>{t('agronomy.thermal_reference.title')}</strong>
              <button type='button' className='btn btn-sm btn-light-primary' disabled={loading}
                onClick={() => onChange((currentProfile) => ({...currentProfile, thermalTime: cloneThermalTime(suggestedThermalTime)}))}>
                {t('agronomy.thermal_time.reset')}
              </button>
            </div>
            <p>{t(`agronomy.thermal_reference.methods.${profile.thermalTime?.method}`)}</p>
            {profile.thermalTime?.source === 'custom' && <p>{t('agronomy.thermal_reference.custom_notice')}</p>}
            <ThermalNumberField label={t('agronomy.thermal_time.base_temperature')} value={profile.thermalTime?.baseTemperatureC}
              min={-10} max={30} step={0.1} unit='°C' disabled={loading}
              onChange={(value) => updateThermalTime((current) => ({...current, baseTemperatureC: editableNumber(value)}))} />
            {!thermalOnlyTimeValid && <small role='alert'>{t('agronomy.thermal_reference.invalid_base')}</small>}
          </section>
          <section className='specific-copilot-field-observations' aria-label={t('agronomy.field_observations.title')}>
            <strong>{t('agronomy.field_observations.title')}</strong>
            <p>{t('agronomy.field_observations.intro')}</p>
            {fieldObservations.map((item, index) => <div className='specific-copilot-field-observations__row' key={index}>
              <AppSelect label={t('agronomy.field_observations.stage')} value={item.stageId} disabled={loading}
                options={observationStageIds.filter((id, stageIndex) => id === item.stageId ||
                  (stageIndex > observationStageIds.indexOf(fieldObservations[index - 1]?.stageId) &&
                    (index + 1 === fieldObservations.length || stageIndex < observationStageIds.indexOf(fieldObservations[index + 1]?.stageId))))
                  .map((id) => ({value: id, label: t(`agronomy.field_observations.stages.${profile.crop}.${id}`)}))}
                onChange={(stageId) => onChange((current) => ({...current, fieldObservations: current.fieldObservations.map((observation, row) => row === index ? {...observation, stageId} : observation)}))} />
              <label className='specific-copilot-agronomy-field'>
                <span>{t('agronomy.field_observations.date')}</span>
                <input type='date' className='form-control form-control-solid' value={item.date || ''}
                  min={profile.sowingDate || undefined} max={profile.harvestDate || maximumSelectableHarvestDate}
                  disabled={loading || !profile.sowingDate}
                  onChange={(event) => onChange((current) => ({...current, fieldObservations: current.fieldObservations.map((observation, row) => row === index ? {...observation, date: event.target.value} : observation)}))} />
              </label>
              <button type='button' className='btn btn-sm btn-light-danger' disabled={loading}
                aria-label={t('agronomy.field_observations.remove')}
                onClick={() => onChange((current) => ({...current, fieldObservations: current.fieldObservations.filter((_, row) => row !== index)}))}>
                {t('agronomy.field_observations.remove')}
              </button>
            </div>)}
            <button type='button' className='btn btn-sm btn-light-primary' disabled={loading || !profile.sowingDate || !availableNewStageIds.length}
              onClick={() => onChange((current) => ({...current, fieldObservations: [...(current.fieldObservations || []), {stageId: availableNewStageIds[0], date: ''}]}))}>
              {t('agronomy.field_observations.add')}
            </button>
            {!fieldObservationsValid && <small role='alert'>{t('agronomy.field_observations.validation_error')}</small>}
          </section>
        </>}

        {!observational && profile.genotypeId && suggestedThermalTime && (
          <section
            className='specific-copilot-thermal-time'
            aria-labelledby='specific-copilot-thermal-time-title'
          >
            <div className='specific-copilot-thermal-time__heading'>
              <div>
                <strong id='specific-copilot-thermal-time-title'>
                  {t('agronomy.thermal_time.title')}
                </strong>
                <p>{t('agronomy.thermal_time.intro')}</p>
              </div>
              <button
                type='button'
                className='btn btn-sm btn-light-primary'
                disabled={loading}
                onClick={() =>
                  onChange((currentProfile) => ({
                    ...currentProfile,
                    thermalTime: cloneThermalTime(suggestedThermalTime),
                  }))
                }
              >
                {t('agronomy.thermal_time.reset')}
              </button>
            </div>

            <p className='specific-copilot-thermal-time__formula'>
              {t('agronomy.thermal_time.formula')}
            </p>

            <div className='specific-copilot-thermal-time__grid'>
              <ThermalNumberField
                label={t('agronomy.thermal_time.base_temperature')}
                value={profile.thermalTime?.baseTemperatureC}
                min='-10'
                max='30'
                step='0.1'
                unit='°C'
                disabled={loading}
                onChange={(value) =>
                  updateThermalTime((thermalTime) => ({
                    ...thermalTime,
                    baseTemperatureC: editableNumber(value),
                  }))
                }
              />

              <ThermalNumberField
                label={t('agronomy.thermal_time.optimum_temperature')}
                value={profile.thermalTime?.optimumTemperatureC}
                min='-9.9'
                max='49.9'
                step='0.1'
                unit='°C'
                disabled={loading}
                onChange={(value) =>
                  updateThermalTime((thermalTime) => ({
                    ...thermalTime,
                    optimumTemperatureC: editableNumber(value),
                  }))
                }
              />

              <ThermalNumberField
                label={t('agronomy.thermal_time.maximum_temperature')}
                value={profile.thermalTime?.maximumTemperatureC}
                min='-9.8'
                max='50'
                step='0.1'
                unit='°C'
                disabled={loading}
                onChange={(value) =>
                  updateThermalTime((thermalTime) => ({
                    ...thermalTime,
                    maximumTemperatureC: editableNumber(value),
                  }))
                }
              />

              {(profile.thermalTime?.stages || []).map((stage, index) => (
                <label className='specific-copilot-agronomy-field' key={stage.stageId}>
                  <span>
                    {t('agronomy.thermal_time.stage_threshold', {
                      stage: t(`agronomy.stages.${stage.stageId || 'unknown'}`),
                    })}
                  </span>
                  <div className='specific-copilot-thermal-time__input'>
                    <input
                      type='number'
                      className='form-control form-control-solid'
                      min='1'
                      max='5000'
                      step='1'
                      value={stage.thresholdGdd ?? ''}
                      disabled={loading}
                      onChange={(event) =>
                        updateThermalTime((thermalTime) => ({
                          ...thermalTime,
                          stages: thermalTime.stages.map((currentStage, stageIndex) =>
                            stageIndex === index
                              ? {
                                  ...currentStage,
                                  thresholdGdd: editableNumber(event.target.value),
                                }
                              : currentStage
                          ),
                        }))
                      }
                    />
                    <span>GDD</span>
                  </div>
                </label>
              ))}
            </div>

            {profile.thermalTime?.photoperiod && (
              <section className='specific-copilot-development-modifier'>
                <div className='specific-copilot-development-modifier__heading'>
                  <div>
                    <strong>{t('agronomy.thermal_time.photoperiod.title')}</strong>
                    <p>{t('agronomy.thermal_time.photoperiod.intro')}</p>
                  </div>
                  <label className='form-check form-switch form-check-custom form-check-solid'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      checked={Boolean(profile.thermalTime.photoperiod.enabled)}
                      disabled={loading}
                      onChange={(event) =>
                        updateThermalTime((thermalTime) => ({
                          ...thermalTime,
                          photoperiod: {
                            ...thermalTime.photoperiod,
                            enabled: event.target.checked,
                          },
                        }))
                      }
                    />
                    <span>{t('agronomy.thermal_time.photoperiod.enabled')}</span>
                  </label>
                </div>
                <div className='specific-copilot-development-modifier__context'>
                  <span>
                    {t('agronomy.thermal_time.photoperiod.response_type')}:{' '}
                    <strong>
                      {t(
                        `agronomy.thermal_time.photoperiod.response_types.${profile.thermalTime.photoperiod.responseType}`
                      )}
                    </strong>
                  </span>
                  <span>
                    {t('agronomy.thermal_time.applies_between', {
                      from: t(
                        `agronomy.stages.${
                          profile.thermalTime.photoperiod.appliesFromStageId || 'unknown'
                        }`
                      ),
                      until: t(
                        `agronomy.stages.${
                          profile.thermalTime.photoperiod.appliesUntilStageId || 'unknown'
                        }`
                      ),
                    })}
                  </span>
                </div>
                <div className='specific-copilot-thermal-time__grid'>
                  <ThermalNumberField
                    label={t('agronomy.thermal_time.photoperiod.critical_hours')}
                    value={profile.thermalTime.photoperiod.criticalHours}
                    min='8'
                    max='18'
                    step='0.1'
                    unit='h'
                    disabled={loading}
                    onChange={(value) =>
                      updateThermalTime((thermalTime) => ({
                        ...thermalTime,
                        photoperiod: {
                          ...thermalTime.photoperiod,
                          criticalHours: editableNumber(value),
                        },
                      }))
                    }
                  />
                  <ThermalNumberField
                    label={t('agronomy.thermal_time.photoperiod.sensitivity')}
                    value={fractionAsPercent(profile.thermalTime.photoperiod.sensitivityPerHour)}
                    min='0'
                    max='100'
                    step='1'
                    unit='%/h'
                    disabled={loading}
                    onChange={(value) =>
                      updateThermalTime((thermalTime) => ({
                        ...thermalTime,
                        photoperiod: {
                          ...thermalTime.photoperiod,
                          sensitivityPerHour: editableFraction(value),
                        },
                      }))
                    }
                  />
                  <ThermalNumberField
                    label={t('agronomy.thermal_time.minimum_development_factor')}
                    value={fractionAsPercent(profile.thermalTime.photoperiod.minimumFactor)}
                    min='10'
                    max='100'
                    step='1'
                    unit='%'
                    disabled={loading}
                    onChange={(value) =>
                      updateThermalTime((thermalTime) => ({
                        ...thermalTime,
                        photoperiod: {
                          ...thermalTime.photoperiod,
                          minimumFactor: editableFraction(value),
                        },
                      }))
                    }
                  />
                </div>
              </section>
            )}

            {profile.thermalTime?.vernalization && (
              <section className='specific-copilot-development-modifier'>
                <div className='specific-copilot-development-modifier__heading'>
                  <div>
                    <strong>{t('agronomy.thermal_time.vernalization.title')}</strong>
                    <p>{t('agronomy.thermal_time.vernalization.intro')}</p>
                  </div>
                  <label className='form-check form-switch form-check-custom form-check-solid'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      checked={Boolean(profile.thermalTime.vernalization.enabled)}
                      disabled={loading}
                      onChange={(event) =>
                        updateThermalTime((thermalTime) => ({
                          ...thermalTime,
                          vernalization: {
                            ...thermalTime.vernalization,
                            enabled: event.target.checked,
                          },
                        }))
                      }
                    />
                    <span>{t('agronomy.thermal_time.vernalization.enabled')}</span>
                  </label>
                </div>
                <div className='specific-copilot-development-modifier__context'>
                  <span>
                    {t('agronomy.thermal_time.applies_between', {
                      from: t(
                        `agronomy.stages.${
                          profile.thermalTime.vernalization.appliesFromStageId || 'unknown'
                        }`
                      ),
                      until: t(
                        `agronomy.stages.${
                          profile.thermalTime.vernalization.appliesUntilStageId || 'unknown'
                        }`
                      ),
                    })}
                  </span>
                </div>
                <div className='specific-copilot-thermal-time__grid'>
                  {[
                    ['minimumTemperatureC', 'minimum_temperature'],
                    ['optimumTemperatureC', 'optimum_temperature'],
                    ['maximumTemperatureC', 'maximum_temperature'],
                  ].map(([field, label]) => (
                    <ThermalNumberField
                      key={field}
                      label={t(`agronomy.thermal_time.vernalization.${label}`)}
                      value={profile.thermalTime.vernalization[field]}
                      min='-10'
                      max='50'
                      step='0.1'
                      unit='°C'
                      disabled={loading}
                      onChange={(value) =>
                        updateThermalTime((thermalTime) => ({
                          ...thermalTime,
                          vernalization: {
                            ...thermalTime.vernalization,
                            [field]: editableNumber(value),
                          },
                        }))
                      }
                    />
                  ))}
                  <ThermalNumberField
                    label={t('agronomy.thermal_time.vernalization.required_units')}
                    value={profile.thermalTime.vernalization.requiredUnits}
                    min='0.1'
                    max='100'
                    step='0.1'
                    unit='UV'
                    disabled={loading}
                    onChange={(value) =>
                      updateThermalTime((thermalTime) => ({
                        ...thermalTime,
                        vernalization: {
                          ...thermalTime.vernalization,
                          requiredUnits: editableNumber(value),
                        },
                      }))
                    }
                  />
                  <ThermalNumberField
                    label={t('agronomy.thermal_time.minimum_development_factor')}
                    value={fractionAsPercent(profile.thermalTime.vernalization.minimumFactor)}
                    min='10'
                    max='100'
                    step='1'
                    unit='%'
                    disabled={loading}
                    onChange={(value) =>
                      updateThermalTime((thermalTime) => ({
                        ...thermalTime,
                        vernalization: {
                          ...thermalTime.vernalization,
                          minimumFactor: editableFraction(value),
                        },
                      }))
                    }
                  />
                </div>
              </section>
            )}

            <div className='specific-copilot-thermal-time__footer'>
              <span>
                {t(`agronomy.thermal_time.sources.${profile.thermalTime?.source || 'suggested'}`)}
              </span>
              {thermalTimeErrorKey && (
                <small role='alert'>
                  {t(`agronomy.thermal_time.errors.${thermalTimeErrorKey}`)}
                </small>
              )}
            </div>
            {suggestedThermalTime.provenance?.modelVersion && (
              <div className='specific-copilot-thermal-time__traceability'>
                <p>
                  {t('agronomy.thermal_time.methodology')}
                </p>
                <p>
                  {t(
                    `agronomy.thermal_time.scope_${
                      profile.thermalTime?.source === 'custom' ? 'custom' : 'suggested'
                    }`
                  )}
                </p>
              </div>
            )}
          </section>
        )}

        <p className='specific-copilot-agronomy-disclaimer'>{t(thermalOnly ? 'agronomy.thermal_reference.notice' : observational ? 'agronomy.observational.notice' : 'agronomy.questionnaire_notice')}</p>
        {hasChanges && (
          <button type='submit' className='btn btn-primary' disabled={!complete || loading}>
            {loading && <span className='spinner-border spinner-border-sm' aria-hidden='true' />}
            {t('agronomy.analyze')}
          </button>
        )}
      </form>
    </section>
  )
}

const SpecificCopilotAgronomy = ({
  open,
  analysis,
  catalog,
  recentNdvi,
  recentEvi,
  profile,
  setProfile,
  onSubmit,
  loading,
}) => {
  const {t} = useTranslation('copilot')
  const [editing, setEditing] = useState(true)
  const activeCrop = analysis?.profile?.crop || profile?.crop
  const observational = isObservationalCrop(activeCrop)
  const thermalOnly = isThermalOnlyAgronomicProfile(analysis?.profile || profile)

  useEffect(() => {
    if (open && analysis?.status === 'available') {
      setEditing(false)
    }
  }, [open, analysis?.status, analysis?.profile?.harvestDate, analysis?.profile?.sowingDate])

  if (analysis?.status === 'available' && !editing) {
    return (
      <AgronomyResult
        analysis={analysis}
        recentNdvi={recentNdvi}
        recentEvi={recentEvi}
        onEdit={() => setEditing(true)}
      />
    )
  }

  return (
    <>
      {analysis?.status && !['available', 'not_requested'].includes(analysis.status) && !loading && (
        <div className='alert alert-warning specific-copilot-agronomy-alert' role='status'>
          {t(thermalOnly ? `agronomy.thermal_reference.errors.${analysis.reason || analysis.status}` : observational ? `agronomy.observational.errors.${analysis.reason || analysis.status}` : `agronomy.errors.${analysis.reason || analysis.status}`, {
            months: getCropAnalysisMonths(activeCrop),
            defaultValue: t(`agronomy.errors.${analysis.reason || analysis.status}`, {defaultValue: t('agronomy.errors.generic')}),
          })}
        </div>
      )}
      <AgronomyQuestionnaire
        catalog={catalog}
        profile={profile || EMPTY_PROFILE}
        analyzedProfile={analysis?.profile}
        onChange={setProfile}
        onSubmit={onSubmit}
        loading={loading}
      />
    </>
  )
}

export {AgronomyResult, EMPTY_PROFILE}
export default SpecificCopilotAgronomy
